import React, { useCallback, useState } from "react";
import videojs from "video.js";
import { useHistory } from "react-router-dom";

import VideoJS from "../../../atoms/VideoJS";
import VideoLessonSideMenu from "../../../organisms/teacher/VideoLessonSideMenu";
import {
  ITeacherLessonRetrospectives,
  LessonVideoChapters,
} from "../../../state";
import { ReactComponent as SelectLessonVideo } from "../../../assets/svg/select_lesson_video.svg";
import { ReactComponent as ArrowDown } from "../../../assets/svg/caret_down.svg";

import styles from "./VideoLesson.module.scss";

interface VideoLessonProps {
  lessonVideoChapters: LessonVideoChapters;
  playingChapterId: number;
  classList: number[];
  prevLessonId: number | null;
  prevLessonFirstChapterId: number | null;
  nextLessonId: number | null;
  nextLessonFirstChapterId: number | null;
  hasLessonCompletion: boolean;
  teacherLessonRetrospectives: ITeacherLessonRetrospectives;
  postCompletedLesson: () => void;
  postCompletedLessonRetrospectives: (
    score: number,
    lessonDeliveryScore: number,
    groupWorkScore: number | null,
  ) => void;
  handleReady: (player: videojs.Player) => void;
  handleFirstVideoPlay: (player: videojs.Player) => void;
  onSwitchToSlide: () => void;
  canSwitchToSlide: boolean;
  isFromProjection?: boolean;
}

const VideoLesson = ({
  lessonVideoChapters,
  playingChapterId,
  classList,
  prevLessonId,
  prevLessonFirstChapterId,
  nextLessonId,
  nextLessonFirstChapterId,
  hasLessonCompletion,
  teacherLessonRetrospectives,
  postCompletedLesson,
  postCompletedLessonRetrospectives,
  handleReady,
  handleFirstVideoPlay,
  onSwitchToSlide,
  canSwitchToSlide,
  isFromProjection,
}: VideoLessonProps) => {
  const history = useHistory();
  const [showNextVideoChapterButton, setShowNextVideoChapterButton] = useState(
    false,
  );
  const [showChapterMenu, setShowChapterMenu] = useState(false);
  const [changeLessonLoading, setChangeLessonLoading] = useState(false);
  const [prevLessonVideoChapters, setPrevLessonVideoChapters] = useState(
    lessonVideoChapters,
  );

  const playingChapter = lessonVideoChapters.video_chapters.find(
    chapter => chapter.id === playingChapterId,
  );

  const sourceUrl =
    process.env.NODE_ENV === "development"
      ? `${process.env.REACT_APP_VIDEO_LESSON_BASE_URL}${playingChapter?.url}`
      : playingChapter?.url;

  const nextChapterIndex = playingChapterId
    ? lessonVideoChapters.video_chapters.findIndex(
        e => e.id === playingChapterId,
      ) + 1
    : 0;

  const nextChapterId =
    nextChapterIndex < lessonVideoChapters.video_chapters.length
      ? lessonVideoChapters.video_chapters[nextChapterIndex].id
      : null;

  const videoOption: videojs.PlayerOptions = {
    controls: true,
    fill: true,
    preload: "metadata",
    controlBar: {
      pictureInPictureToggle: false,
    },
    playbackRates: [0.5, 1.0, 1.25, 1.5, 2.0],
    sources: [
      {
        src: sourceUrl + ".m3u8",
        type: "application/x-mpegURL",
      },
    ],
  };

  if (prevLessonVideoChapters !== lessonVideoChapters) {
    setPrevLessonVideoChapters(lessonVideoChapters);
    setChangeLessonLoading(false);
  }

  const onChangeLesson = (lessonId: number, chapterId: number) => {
    setPrevLessonVideoChapters(lessonVideoChapters);
    setChangeLessonLoading(true);
    history.push({
      search: `?lesson=${lessonId}&chapter=${chapterId}&classList=${classList}`,
    });
  };

  const prevLesson = () => {
    prevLessonId &&
      prevLessonFirstChapterId &&
      onChangeLesson(prevLessonId, prevLessonFirstChapterId);
  };

  const nextLesson = () => {
    nextLessonId &&
      nextLessonFirstChapterId &&
      onChangeLesson(nextLessonId, nextLessonFirstChapterId);
  };

  // サイドメニューでモーダルを表示すると関数が更新され
  // 動画が再生されてしまうためuseCallbackを使用
  const handleVideoStart = useCallback((player: videojs.Player) => {
    const playerElement = player
      .el()
      .querySelector("video") as HTMLVideoElement;
    playerElement.style.pointerEvents = "auto";

    setShowNextVideoChapterButton(false);
  }, []);

  // サイドメニューでモーダルを表示すると関数が更新され
  // 動画が再生されてしまうためuseCallbackを使用
  const handleVideoEnd = useCallback(
    (player: videojs.Player) => {
      player.isFullscreen() && player.exitFullscreen();

      const playerElement = player
        .el()
        .querySelector("video") as HTMLVideoElement;
      playerElement.style.pointerEvents = "none";

      nextChapterId
        ? setShowNextVideoChapterButton(true)
        : setShowChapterMenu(true);
    },
    [nextChapterId],
  );

  const videoDisplay = () => {
    if (changeLessonLoading) {
      return null;
    }

    if (!playingChapter || !sourceUrl) {
      return (
        <div className={styles.selectLessonVideoWrapper}>
          <SelectLessonVideo />
          <p>動画を選択してください</p>
        </div>
      );
    }

    return (
      <>
        <VideoJS
          className={styles.vjs}
          options={videoOption}
          showVideo={true}
          sourceUrl={sourceUrl}
          onReady={handleReady}
          onFirstVideoPlay={handleFirstVideoPlay}
          onVideoStart={handleVideoStart}
          onVideoEnd={handleVideoEnd}
        />
        {showNextVideoChapterButton && (
          <div
            className={styles.nextChapterButton}
            onClick={() => {
              setShowNextVideoChapterButton(false);
              history.push({
                search: `?lesson=${lessonVideoChapters.id}&chapter=${nextChapterId}&classList=${classList}`,
              });
            }}
          >
            <ArrowDown />
            <p>次のチャプターへ</p>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="d-flex h-100">
      <div className={styles.wrapper}>{videoDisplay()}</div>
      <VideoLessonSideMenu
        key={playingChapterId}
        lessonId={lessonVideoChapters.id}
        lessonName={lessonVideoChapters.name}
        lessonTitle={lessonVideoChapters.title}
        teachingMaterialId={lessonVideoChapters.teaching_material_id}
        teachingMaterialName={lessonVideoChapters.teaching_material_name}
        isDisplayAllSchoolsAnswer={
          lessonVideoChapters.is_display_all_schools_answer
        }
        videoChapters={lessonVideoChapters.video_chapters}
        playingChapterId={playingChapterId}
        nextChapterId={nextChapterId}
        classList={classList}
        prevLessonId={prevLessonId}
        nextLessonId={nextLessonId}
        hasLessonCompletion={hasLessonCompletion}
        teacherLessonRetrospectives={teacherLessonRetrospectives}
        showChapterMenu={showChapterMenu}
        postCompletedLesson={postCompletedLesson}
        postCompletedLessonRetrospectives={postCompletedLessonRetrospectives}
        prevLesson={prevLesson}
        nextLesson={nextLesson}
        setShowChapterMenu={setShowChapterMenu}
        onSwitchToSlide={onSwitchToSlide}
        canSwitchToSlide={canSwitchToSlide}
        isFromProjection={isFromProjection}
      />
    </div>
  );
};

export default VideoLesson;
